import { Drawer } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Add as AddIcon,
  CloseOutlined,
  Remove as RemoveIcon,
  ShoppingBagOutlined,
} from '@mui/icons-material';

import { RootState } from '../../../redux';
import { closeCart, removeLine, setLineQuantity } from '../../../redux/cart';
import { unitPriceFor } from '../../../api/product';
import { resolveImage } from '../../../api/axiosIntance';
import { Eyebrow } from '../../atome/motion/motion.styled';
import {
  BagBody,
  BagFoot,
  BagHead,
  CheckoutCta,
  CloseButton,
  EmptyBag,
  FootNote,
  KeepShoppingCta,
  Line,
  LineBody,
  LineFoot,
  LineImage,
  LinePrice,
  RemoveLink,
  SavingNote,
  SoldOutTag,
  Stepper,
  TotalLine,
  drawerPaperSx,
} from './cart.styled';

const FALLBACK_IMAGE = '/img/klarity-product.jpg';

/**
 * Slide-over bag. Lives in the header so it is reachable from every page, and
 * hands off to /checkout, which reads the same lines.
 */
const CartDrawer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { lines, open } = useSelector((state: RootState) => state.cart);
  const hasEmail = Boolean(useSelector((state: RootState) => state.user?.email));

  const total = lines.reduce((sum, l) => sum + unitPriceFor(l.product, hasEmail) * l.quantity, 0);
  const listTotal = lines.reduce((sum, l) => sum + l.product.price * l.quantity, 0);
  const savings = listTotal - total;
  const hasSoldOut = lines.some((l) => l.product.stock <= 0);

  const goToCheckout = () => {
    dispatch(closeCart());
    navigate('/checkout');
  };

  // Already on the shop? Only close the bag. Scrolling anywhere would throw
  // away the spot they were browsing, which is exactly where they want to be
  // to add the next product.
  const keepShopping = () => {
    dispatch(closeCart());

    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => dispatch(closeCart())}
      slotProps={{ paper: { sx: drawerPaperSx } }}
    >
      <BagHead>
        <div>
          <Eyebrow>Klarity</Eyebrow>
          <h2>Your bag</h2>
        </div>
        <CloseButton type="button" onClick={() => dispatch(closeCart())} aria-label="Close bag">
          <CloseOutlined sx={{ fontSize: 20 }} />
        </CloseButton>
      </BagHead>

      <BagBody>
        {lines.length === 0 ? (
          <EmptyBag>
            <ShoppingBagOutlined sx={{ fontSize: 40, color: '#c3b5a3' }} />
            <h3>Nothing in here yet</h3>
            <p>Add a product and it will wait for you right here.</p>
            <CheckoutCta type="button" onClick={keepShopping}>
              Browse the shop
            </CheckoutCta>
          </EmptyBag>
        ) : (
          lines.map(({ product, quantity }) => {
            const unit = unitPriceFor(product, hasEmail);
            const soldOut = product.stock <= 0;

            return (
              <Line key={product.id} $muted={soldOut}>
                <LineImage
                  src={resolveImage(product.imageUrl?.[0]) || FALLBACK_IMAGE}
                  alt={product.name}
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE;
                  }}
                />

                <LineBody>
                  <h3>{product.name}</h3>
                  <p>
                    ${unit.toFixed(2)} each
                    {unit < product.price && <del>${product.price.toFixed(2)}</del>}
                  </p>

                  {soldOut ? (
                    <>
                      <SoldOutTag>Sold out</SoldOutTag>
                      <LineFoot>
                        <RemoveLink type="button" onClick={() => dispatch(removeLine(product.id))}>
                          Remove
                        </RemoveLink>
                      </LineFoot>
                    </>
                  ) : (
                    <LineFoot>
                      <Stepper>
                        <button
                          type="button"
                          aria-label={`Less ${product.name}`}
                          onClick={() =>
                            dispatch(setLineQuantity({ productId: product.id, quantity: quantity - 1 }))
                          }
                        >
                          <RemoveIcon sx={{ fontSize: 14 }} />
                        </button>
                        <span>{quantity}</span>
                        <button
                          type="button"
                          aria-label={`More ${product.name}`}
                          disabled={quantity >= product.stock}
                          onClick={() =>
                            dispatch(setLineQuantity({ productId: product.id, quantity: quantity + 1 }))
                          }
                        >
                          <AddIcon sx={{ fontSize: 14 }} />
                        </button>
                      </Stepper>

                      <LinePrice>${(unit * quantity).toFixed(2)}</LinePrice>
                    </LineFoot>
                  )}

                  {!soldOut && quantity >= product.stock && product.stock <= 5 && (
                    <SoldOutTag as="span">Only {product.stock} left</SoldOutTag>
                  )}
                </LineBody>
              </Line>
            );
          })
        )}
      </BagBody>

      {lines.length > 0 && (
        <BagFoot>
          <TotalLine>
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </TotalLine>

          {savings > 0 && <SavingNote>You save ${savings.toFixed(2)}</SavingNote>}

          <KeepShoppingCta type="button" onClick={keepShopping}>
            Keep shopping
          </KeepShoppingCta>

          <CheckoutCta type="button" onClick={goToCheckout} disabled={hasSoldOut}>
            Checkout
          </CheckoutCta>

          <FootNote>
            {hasSoldOut
              ? 'Remove the sold-out item to continue.'
              : 'Free delivery across Lebanon · pay when it arrives'}
          </FootNote>
        </BagFoot>
      )}
    </Drawer>
  );
};

export default CartDrawer;
